import React, {
    useState,
    FocusEvent,
    ChangeEventHandler,
    CSSProperties,
    FocusEventHandler,
    useEffect,
    forwardRef,
    useImperativeHandle,
    Ref,
    useRef
} from "react";

import AppInputStyles from "./AppInput.module.scss";
import HelperStyles from "../../scss/helper.module.scss";
import ValidationError from "../validation-error/ValidationError";
import { getHeightByViewPort } from "../../methods/getSizeByViewPort";
import classNames from "classnames";

export interface AppInputRef {
    focus: () => void;
}

export interface AppInputProps<T> {
    id?: string;
    name: string;
    type: "text" | "number" | "email" | "password";
    isRequired?: boolean;
    labelText: string;
    valuePrefix?: string;
    valueSuffix?: string | React.ReactElement;
    value: T;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    error?: string;
    isActiveByDefault?: boolean;
    containerStyles?: CSSProperties;
    labelStyles?: CSSProperties;
    inputWrapperStyles?: CSSProperties;
    inputStyles?: CSSProperties;
    prefixStyles?: CSSProperties;
    suffixStyles?: CSSProperties;
    isValid?: boolean;
    placeholder?: string;
    alwaysShowPrefix?: boolean;
    alwaysShowSuffix?: boolean;
    setFocus?: number;
    autoComplete?: boolean;
    className?: string;
    containerClassName?: string;
    labelClassName?: string;
    inputWrapperClassName?: string;
    inputClassName?: string;
    prefixClassName?: string;
    suffixClassName?: string;
    activeClassName?: string;
    dirtyClassName?: string;
    showValidationErrOnChange?: boolean;
    valuePrefixClass?: any;
    disabled?: boolean;
    errorClass?: string;
    maxlength?: number;
    // ref: Ref<AppInputRef>;
}

// export default forwardRef(
//     <T extends string | number | readonly string[] | undefined>(props: AppInputProps<T>, ref: Ref<AppInputRef>) => (
//         <AppInput {...props} ref={ref} />
//     )
// );

const AppInput = forwardRef(
    <T extends string | number | readonly string[] | undefined>(
        {
            name,
            type,
            isRequired = false,
            labelText,
            valuePrefix,
            valueSuffix,
            value,
            onChange,
            onBlur,
            onFocus,
            error,
            isActiveByDefault = false,
            labelStyles = {},
            containerStyles = {},
            inputWrapperStyles = {},
            prefixStyles = {},
            suffixStyles = {},
            inputStyles = {},
            isValid = true,
            placeholder,
            alwaysShowPrefix,
            alwaysShowSuffix,
            setFocus = 0,
            autoComplete = true,
            className,
            containerClassName,
            labelClassName,
            inputWrapperClassName,
            inputClassName,
            prefixClassName,
            suffixClassName,
            activeClassName,
            dirtyClassName,
            showValidationErrOnChange = true,
            disabled = false,
            errorClass,
            maxlength,
            id
        }: AppInputProps<T>,
        ref: Ref<AppInputRef>
    ) => {
        const [isActive, setIsActive] = useState(false);
        const [isDirty, setIsDirty] = useState(false);
        const inputRef = useRef<HTMLInputElement | null>(null);
        // const numField = useField({ name, type });

        const _onFocus = (e: FocusEvent<HTMLInputElement>) => {
            onFocus?.(e);
            setIsActive(true);
        };

        const _onBlur = (e: FocusEvent<HTMLInputElement>) => {
            e.persist();
            setIsDirty(!!e.target.value);
            setIsActive(false);
            onBlur?.(e);
        };

        const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            e.persist();
            showValidationErrOnChange && setIsDirty(!!e.target.value);
            onChange?.(e);
        };

        useEffect(() => {
            if (setFocus) {
                setTimeout(() => {
                    // elem?.focus();
                    inputRef.current?.focus();
                }, 1);
            }
        }, [setFocus]);

        const focus = () => {
            setTimeout(() => {
                // elem?.focus();
                inputRef.current?.focus();
            }, 1);
        };

        useImperativeHandle(ref, () => ({
            focus
        }));

        useEffect(() => {
            // isActiveByDefault &&
            //     setTimeout(() => {
            //         console.log("elem", elem);
            //         elem?.focus();
            //     }, 1);
            if (isActiveByDefault && inputRef.current) {
                inputRef.current.focus();
            }
        }, [isActiveByDefault]);

        return (
            <div className={classNames(disabled && AppInputStyles.disabled, className)}>
                <div
                    // className={classNames(
                    //     containerClassName,
                    //     HelperStyles.center_vertical +
                    //         " " +
                    //         AppInputStyles.field_group +
                    //         " " +
                    //         (isActive ? AppInputStyles.active : "") +
                    //         " " +
                    //         ((isDirty || value) && !isValid ? AppInputStyles.invalid : ""),
                    //     containerClassName
                    // )}
                    className={classNames(
                        HelperStyles.center_vertical,
                        AppInputStyles.field_group,
                        isActive && AppInputStyles.active,
                        isActive && activeClassName,
                        (isDirty || value) && error && AppInputStyles.invalid,
                        containerClassName
                    )}
                    style={containerStyles}
                >
                    <label
                        htmlFor={name}
                        // hidden={activeField !== "name" && !isDirty}
                        className={classNames(
                            isActive
                                ? AppInputStyles.active + " " + activeClassName
                                : isDirty || value
                                ? AppInputStyles.dirty + " " + dirtyClassName
                                : "",
                            labelClassName
                        )}
                        style={labelStyles}
                    >
                        {labelText}
                        {isRequired && <span className={AppInputStyles.required}> *</span>}
                    </label>
                    <div
                        style={{ ...inputWrapperStyles }}
                        className={classNames(AppInputStyles.input_wrapper, inputWrapperClassName)}
                    >
                        <span
                            style={{ marginRight: valuePrefix && "9px", ...inputStyles }}
                            hidden={!alwaysShowPrefix && !isActive && !(isDirty || value)}
                            className={classNames(AppInputStyles.value_prefix, inputClassName, prefixClassName)}
                        >
                            {valuePrefix}
                        </span>
                        <input
                            // ref={isActiveByDefault ? refFunc : null}
                            // ref={refFunc}
                            id={id}
                            ref={inputRef}
                            name={name}
                            type={type}
                            onChange={_onChange}
                            onFocus={_onFocus}
                            onBlur={_onBlur}
                            className={classNames(AppInputStyles.input, inputClassName)}
                            value={value}
                            style={inputStyles}
                            placeholder={placeholder}
                            autoFocus={isActiveByDefault}
                            autoComplete={!autoComplete ? "off" : undefined}
                            disabled={disabled}
                            maxLength={maxlength}
                            onWheel={(event) => event.currentTarget.blur()}
                        />
                        <span
                            style={{ marginLeft: valueSuffix && "9px", ...suffixStyles }}
                            hidden={!alwaysShowSuffix && !isActive && !(isDirty || value)}
                            className={classNames(AppInputStyles.value_suffix, suffixClassName)}
                        >
                            {valueSuffix}
                        </span>
                    </div>
                </div>
                {error && (
                    <ValidationError
                        error={isDirty ? error : ""}
                        style={{ marginTop: getHeightByViewPort(8) }}
                        errorClass={classNames(errorClass)}
                    />
                )}
            </div>
        );
    }
);

export default AppInput;
